import { DEFAULT_FONT_CONFIG, FONT_SCALE_LEVELS } from "@constants";
import { t } from "i18next";
import { useSnapshot } from "valtio";

import React from "react";

import { isValidFontName } from "@helpers/fontConfig";

import { ScrollContainer } from "@components/ScrollContainer";

import { kioskStore as store } from "@pages/Kiosk/store";

import { ResetFontConfirmationModal } from "./ResetFontConfirmationModal";

function toTitleCase(value: string): string {
    return value
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}

const DEFAULT_FONT_OPTIONS = [
    "Roboto",
    "Open Sans",
    "Poppins",
    "Nunito",
    "Montserrat",
    "Bebas Neue",
];

const MAX_FONT_OPTIONS = 6;

const BASE_SIZE_MIN = 14;
const BASE_SIZE_MAX = 32;

const DEFAULT_COLORS: FontColorsType = {
    ...(DEFAULT_FONT_CONFIG.colors ?? {}),
};

export function Miscellaneous(): JSX.Element {
    const { activeTabTitle, localProject } = useSnapshot(store);

    const generalDesign = localProject.template.content
        .generalDesign as GeneralDesignType;
    const currentFont = generalDesign.fonts?.[0];

    // Derived from the store — single source of truth (no duplicated local state).
    const fontFamily = currentFont?.font || DEFAULT_FONT_CONFIG.font;
    const baseSize = currentFont?.size ?? DEFAULT_FONT_CONFIG.size;
    const textColors: FontColorsType = {
        ...DEFAULT_COLORS,
        ...(currentFont?.colors || {}),
    };

    // Genuinely UI-only state (transient input, validation error, option list).
    const [fontOptions, setFontOptions] = React.useState<string[]>(() => {
        if (!DEFAULT_FONT_OPTIONS.includes(fontFamily)) {
            return [
                fontFamily,
                ...DEFAULT_FONT_OPTIONS.slice(0, MAX_FONT_OPTIONS - 1),
            ];
        }
        return [...DEFAULT_FONT_OPTIONS];
    });
    const [customFont, setCustomFont] = React.useState<string>("");
    const [customFontError, setCustomFontError] = React.useState<string>("");

    // Optimistic color buffer — <input type=color> fires onChange continuously
    // during drag; we keep the preview snappy but debounce the store commit
    // to avoid flooding Valtio + CSS var updates on every intermediate value.
    const [pendingColors, setPendingColors] =
        React.useState<FontColorsType | null>(null);
    const colorDebounceRef = React.useRef<number | null>(null);
    const displayColors: FontColorsType = pendingColors ?? textColors;

    const [isResetConfirmationOpen, setIsResetConfirmationOpen] =
        React.useState<boolean>(false);

    React.useEffect(() => {
        return () => {
            if (colorDebounceRef.current != null) {
                window.clearTimeout(colorDebounceRef.current);
            }
        };
    }, []);

    const COLOR_DEBOUNCE_MS = 200;

    function commitFont(patch: Partial<GeneralDesignFontType>) {
        const target = store.localProject.template.content
            .generalDesign as GeneralDesignType;
        const fonts = [...(target.fonts || [])];
        const base = fonts[0] ?? {
            font: DEFAULT_FONT_CONFIG.font,
            title: "primary",
            size: DEFAULT_FONT_CONFIG.size,
            colors: { ...DEFAULT_COLORS },
        };
        fonts[0] = { ...base, ...patch };
        target.fonts = fonts;
    }

    function handleSelectFont(font: string) {
        if (!isValidFontName(font)) return;
        commitFont({ font });
        setCustomFont("");
    }

    function handleCustomFontApply() {
        const trimmed = customFont.trim();
        if (!trimmed) return;
        if (!isValidFontName(trimmed)) {
            setCustomFontError(t("Invalid font name"));
            return;
        }
        const normalized = toTitleCase(trimmed);
        setCustomFontError("");
        if (!fontOptions.includes(normalized)) {
            setFontOptions([
                normalized,
                ...fontOptions.slice(0, MAX_FONT_OPTIONS - 1),
            ]);
        }
        commitFont({ font: normalized });
        setCustomFont("");
    }

    function handleSizeChange(size: number) {
        commitFont({ size });
    }

    function handleColorChange(level: keyof FontColorsType, color: string) {
        const next: FontColorsType = { ...displayColors, [level]: color };
        setPendingColors(next);
        if (colorDebounceRef.current != null) {
            window.clearTimeout(colorDebounceRef.current);
        }
        colorDebounceRef.current = window.setTimeout(() => {
            commitFont({ colors: next });
            setPendingColors(null);
            colorDebounceRef.current = null;
        }, COLOR_DEBOUNCE_MS);
    }

    function flushColorCommit() {
        if (colorDebounceRef.current == null || !pendingColors) return;
        window.clearTimeout(colorDebounceRef.current);
        colorDebounceRef.current = null;
        commitFont({ colors: pendingColors });
        setPendingColors(null);
    }

    function handleResetDefaults() {
        if (colorDebounceRef.current != null) {
            window.clearTimeout(colorDebounceRef.current);
            colorDebounceRef.current = null;
        }
        setPendingColors(null);
        commitFont({
            font: DEFAULT_FONT_CONFIG.font,
            size: DEFAULT_FONT_CONFIG.size,
            colors: { ...DEFAULT_COLORS },
        });
        setCustomFont("");
        setCustomFontError("");
        setFontOptions([...DEFAULT_FONT_OPTIONS]);
    }

    const handleOpenResetConfirmation = React.useCallback(() => {
        setIsResetConfirmationOpen(true);
    }, []);

    const handleConfirmReset = React.useCallback(() => {
        handleResetDefaults();
        setIsResetConfirmationOpen(false);
    }, []);

    const handleCancelReset = React.useCallback(() => {
        setIsResetConfirmationOpen(false);
    }, []);

    return (
        <div className="d-flex flex-column" style={{ margin: 30 }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 30,
                    marginLeft: 23,
                    marginRight: 23,
                }}
            >
                <span
                    style={{
                        fontWeight: 600,
                        fontSize: "var(--font-size-subtitle)",
                        lineHeight: "49px",
                    }}
                >
                    {activeTabTitle}
                </span>
                <button
                    type="button"
                    onClick={handleOpenResetConfirmation}
                    aria-label={t("Reset defaults") as string}
                    data-testid="font-reset-defaults"
                    style={{
                        padding: "8px 16px",
                        borderRadius: "8px",
                        border: "1px solid #dc3545",
                        background: "#ffffff",
                        color: "#dc3545",
                        fontSize: "var(--font-size-small)",
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                    }}
                >
                    {t("Reset defaults")}
                </button>
            </div>

            <ScrollContainer
                style={{
                    maxHeight: "880px",
                    overflowY: "auto",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "22px",
                        margin: "0 23px",
                    }}
                >
                    {/* ── Font family ── */}
                    <div
                        style={{
                            background: "#f8f9fa",
                            borderRadius: "10px",
                            padding: "20px",
                            border: "1px solid #e9ecef",
                        }}
                    >
                        <div
                            style={{
                                fontWeight: 600,
                                fontSize: "var(--font-size-base)",
                                marginBottom: "12px",
                                color: "#333",
                            }}
                        >
                            {t("Font family")}
                        </div>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr",
                                gap: "8px",
                                marginBottom: "12px",
                            }}
                        >
                            {fontOptions.map((font: string) => (
                                <button
                                    key={font}
                                    type="button"
                                    onClick={() => handleSelectFont(font)}
                                    aria-label={`${t("Font family")}: ${font}`}
                                    aria-pressed={fontFamily === font}
                                    data-testid={`font-option-${font}`}
                                    style={{
                                        padding: "10px 12px",
                                        borderRadius: "8px",
                                        border:
                                            fontFamily === font
                                                ? "2px solid #077dbc"
                                                : "1px solid #dee2e6",
                                        background:
                                            fontFamily === font
                                                ? "#e8f4fd"
                                                : "#ffffff",
                                        cursor: "pointer",
                                        fontFamily: `${font}, sans-serif`,
                                        fontSize: "var(--font-size-small)",
                                        fontWeight:
                                            fontFamily === font ? 600 : 400,
                                        color:
                                            fontFamily === font
                                                ? "#077dbc"
                                                : "#495057",
                                        transition: "all 0.15s ease",
                                    }}
                                >
                                    {font}
                                </button>
                            ))}
                        </div>

                        <div
                            style={{
                                display: "flex",
                                gap: "8px",
                                alignItems: "center",
                            }}
                        >
                            <input
                                type="text"
                                value={customFont}
                                onChange={(e) => {
                                    setCustomFont(e.target.value);
                                    if (customFontError) setCustomFontError("");
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter")
                                        handleCustomFontApply();
                                }}
                                placeholder={t("Custom font name") as string}
                                aria-label={t("Custom font name") as string}
                                aria-invalid={Boolean(customFontError)}
                                data-testid="font-custom-input"
                                style={{
                                    flex: 1,
                                    padding: "10px 12px",
                                    borderRadius: "8px",
                                    border: customFontError
                                        ? "1px solid #dc3545"
                                        : "1px solid #dee2e6",
                                    fontSize: "var(--font-size-small)",
                                    outline: "none",
                                }}
                            />
                            <button
                                type="button"
                                onClick={handleCustomFontApply}
                                aria-label={t("Apply custom font") as string}
                                data-testid="font-custom-apply"
                                style={{
                                    padding: "10px 20px",
                                    borderRadius: "8px",
                                    border: "none",
                                    background: "#077dbc",
                                    color: "#ffffff",
                                    fontSize: "var(--font-size-small)",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                }}
                            >
                                {t("Apply")}
                            </button>
                        </div>
                        {customFontError && (
                            <div
                                style={{
                                    marginTop: "8px",
                                    color: "#dc3545",
                                    fontSize: "var(--font-size-small)",
                                }}
                            >
                                {customFontError}
                            </div>
                        )}
                    </div>

                    {/* ── Base size ── */}
                    <div
                        style={{
                            background: "#f8f9fa",
                            borderRadius: "10px",
                            padding: "20px",
                            border: "1px solid #e9ecef",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "12px",
                            }}
                        >
                            <span
                                style={{
                                    fontWeight: 600,
                                    fontSize: "var(--font-size-base)",
                                    color: "#333",
                                }}
                            >
                                {t("Base font size")}
                            </span>
                            <span
                                style={{
                                    fontWeight: 700,
                                    fontSize: "var(--font-size-base)",
                                    color: "#077dbc",
                                    background: "#e8f4fd",
                                    padding: "2px 10px",
                                    borderRadius: "16px",
                                }}
                            >
                                {baseSize}
                            </span>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                            }}
                        >
                            <span
                                style={{ fontSize: "var(--font-size-small)" }}
                            >
                                A
                            </span>
                            <input
                                type="range"
                                min={BASE_SIZE_MIN}
                                max={BASE_SIZE_MAX}
                                value={baseSize}
                                onChange={(e) =>
                                    handleSizeChange(Number(e.target.value))
                                }
                                aria-label={t("Base font size") as string}
                                data-testid="font-base-size"
                                style={{
                                    flex: 1,
                                    height: "6px",
                                    accentColor: "#077dbc",
                                    cursor: "pointer",
                                }}
                            />
                            <span style={{ fontSize: "var(--font-size-body)" }}>
                                A
                            </span>
                        </div>
                    </div>

                    {/* ── Text colors ── */}
                    <div
                        style={{
                            background: "#f8f9fa",
                            borderRadius: "10px",
                            padding: "20px",
                            border: "1px solid #e9ecef",
                        }}
                    >
                        <div
                            style={{
                                fontWeight: 600,
                                fontSize: "var(--font-size-base)",
                                marginBottom: "12px",
                                color: "#333",
                            }}
                        >
                            {t("Text colors")}
                        </div>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px",
                            }}
                        >
                            {FONT_SCALE_LEVELS.map((level) => (
                                <div
                                    key={level}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        padding: "8px 12px",
                                        borderRadius: "6px",
                                        background: "#ffffff",
                                        border: "1px solid #e9ecef",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "1rem",
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize:
                                                    "var(--font-size-small)",
                                                color: "#6c757d",
                                                width: "6rem",
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            {t(level)}
                                        </span>
                                        <span
                                            style={{
                                                fontSize:
                                                    "var(--font-size-base)",
                                                color: displayColors[
                                                    level as keyof FontColorsType
                                                ],
                                                fontFamily: `${fontFamily}, sans-serif`,
                                            }}
                                        >
                                            {t("Sample text")}
                                        </span>
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize:
                                                    "var(--font-size-small)",
                                                color: "#adb5bd",
                                            }}
                                        >
                                            {
                                                displayColors[
                                                    level as keyof FontColorsType
                                                ]
                                            }
                                        </span>
                                        <input
                                            type="color"
                                            value={
                                                displayColors[
                                                    level as keyof FontColorsType
                                                ]
                                            }
                                            onChange={(e) =>
                                                handleColorChange(
                                                    level as keyof FontColorsType,
                                                    e.target.value
                                                )
                                            }
                                            onBlur={flushColorCommit}
                                            aria-label={`${t("Text color")} ${t(
                                                level
                                            )}`}
                                            data-testid={`font-color-${level}`}
                                            style={{
                                                width: "32px",
                                                height: "32px",
                                                border: "1px solid #dee2e6",
                                                borderRadius: "6px",
                                                cursor: "pointer",
                                                padding: "2px",
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Placeholder for future settings ── */}
                </div>
            </ScrollContainer>

            <ResetFontConfirmationModal
                isOpen={isResetConfirmationOpen}
                onConfirm={handleConfirmReset}
                onCancel={handleCancelReset}
            />
        </div>
    );
}
